import moment from "moment";

// ─────────────────────────────────────────────────────────────────────────────
// Date/Time Parsing
// ─────────────────────────────────────────────────────────────────────────────

/** Sentinel values the API returns when no real date/time exists */
const INVALID_DATE_SENTINELS = new Set([
  "", "0", "0.00", "0.000", "DD/MM/YYYY", "null", "undefined",
]);

/** All date formats the API may return, ordered from most specific to least */
const DATE_FORMATS = [
  "YYYY-MM-DDTHH:mm:ss.SSSZ",
  "YYYY-MM-DD HH:mm:ss.S",
  "DD/MM/YYYY HH:mm",
  "YYYY-MM-DD HH:mm",
  moment.ISO_8601,
];

/**
 * Normalise any raw date value from the API into "DD/MM/YYYY HH:mm" (Local Time)
 * or null when the value is empty / sentinel / unparseable.
 *
 * This is the single source-of-truth for "is this date actually populated?".
 */
export function formatToLT(raw) {
  if (raw == null || raw === false) return null;

  // Fast-path: numeric zero
  if (raw === 0) return null;

  const str = String(raw).trim();
  if (!str || INVALID_DATE_SENTINELS.has(str)) return null;

  const m = moment(str, DATE_FORMATS, true); // strict mode
  if (m.isValid()) return m.format("DD/MM/YYYY HH:mm");

  // Retry without strict (some API strings carry unexpected trailing data)
  const lenient = moment(str, DATE_FORMATS);
  return lenient.isValid() ? lenient.format("DD/MM/YYYY HH:mm") : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Numeric Quantity Parsing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Return a numeric value, preferring `primary` when it is a meaningful number,
 * otherwise falling back to `fallback`, defaulting to 0.
 */
export function parseQty(primary, fallback) {
  if (isValidQty(primary)) return Number(primary);
  if (isValidQty(fallback)) return Number(fallback);
  return 0;
}

function isValidQty(val) {
  return val !== undefined && val !== null && val !== "" && val !== 0 && val !== "0" && !Number.isNaN(Number(val));
}

// ─────────────────────────────────────────────────────────────────────────────
// Estimate Port Matching
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Given an operations cargo row, find the best-matching estimate port entry.
 *
 * Match priority:
 *   1. Port name + operation type (exact, case-insensitive)
 *   2. Port name only
 *   3. Same index (positional fallback)
 *   4. Empty object (safe destructuring)
 */
export function findEstimateItem(cargoItem, index, estimatePortList = []) {
  if (!estimatePortList?.length) return {};

  const portName = (cargoItem?.port || "").toLowerCase();
  const operation = (cargoItem?.operation || "").toLowerCase();

  const matchPort = (est) => {
    const p1 = (est?.openingPort || "").toLowerCase();
    const p2 = (est?.port || "").toLowerCase();
    return p1 === portName || p2 === portName;
  };

  // 1. Port + Operation match
  const byPortAndOp = estimatePortList.find(
    (est) => matchPort(est) && (est?.operation || "").toLowerCase() === operation
  );
  if (byPortAndOp) return byPortAndOp;

  // 2. Port-only match
  const byPort = estimatePortList.find(matchPort);
  if (byPort) return byPort;

  // 3. Positional fallback
  return estimatePortList[index] || {};
}

// ─────────────────────────────────────────────────────────────────────────────
// Detailed Output Extraction
// ─────────────────────────────────────────────────────────────────────────────

/** Known array keys that hold per-port leg data inside a detailed_output vessel entry */
const PORT_LIST_KEYS = [
  "cargoPortToPortOperationsOrders",
  "cargoList",
  "vesselVoyageData",
  "legResults",
  "portCalculations",
];

/**
 * Extract the estimate port list from `data`, searching through several
 * possible nesting paths the API returns.
 *
 * Returns an array (possibly empty) of port/leg objects.
 */
export function extractEstimatePortList(data) {
  if (!data) return [];

  // 1. Try detailed_output / output (keyed by vessel UUID)
  const detailedOutput =
    data?.detailed_output ||
    data?.operationEstimateContract?.detailed_output ||
    data?.output ||
    data?.operationEstimateContract?.output ||
    null;

  if (detailedOutput && typeof detailedOutput === "object") {
    for (const key of Object.keys(detailedOutput)) {
      const vesselObj = detailedOutput[key];
      if (!vesselObj || typeof vesselObj !== "object") continue;

      for (const listKey of PORT_LIST_KEYS) {
        if (vesselObj[listKey]?.length) {
          return vesselObj[listKey];
        }
      }
    }
  }

  // 2. Fallback paths inside contract details
  return (
    data?.operationEstimateContract?.cargoContractDetails?.cargoDetails?.cargoList ||
    data?.operationEstimateContract?.vesselContractDetails?.vesselDetails?.clientQuery?.cargoList ||
    data?.cargoContractDetails?.actualCargoContract?.cargoDetails?.cargoList ||
    data?.vesselContractDetails?.actualVesselContract?.vesselDetails?.clientQuery?.cargoList ||
    []
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Resolve date from a cargo row or its estimate counterpart
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolve the best available date for arrival or departure by trying multiple
 * field paths in priority order.
 *
 * @param {"arrival"|"departure"} direction
 * @param {object} item        - current operations cargo row
 * @param {object} estItem     - matched estimate row
 * @returns {string|null}      - "DD/MM/YYYY HH:mm" or null
 */
function resolveDateTime(direction, item, estItem) {
  const isArrival = direction === "arrival";

  // Fields on the operations row itself
  const primaryField = isArrival ? "arrivalTime" : "departureTime";
  const cargoDetailField = isArrival ? "arrivalTime" : "departureTime";

  // 1. Operations row
  const fromItem =
    formatToLT(item?.[primaryField]) ||
    formatToLT(item?.cargoDetails?.[cargoDetailField]);
  if (fromItem) return fromItem;

  // 2. Estimate row — try all known field locations
  const estApiField = isArrival ? "arrivalDateAndTime" : "departureDateAndTime";
  const estLegacyField = primaryField;
  const estStayField = primaryField;

  const fromEst =
    formatToLT(estItem?.[estApiField]) ||
    formatToLT(estItem?.[estLegacyField]) ||
    formatToLT(estItem?.cargoDetails?.[cargoDetailField]) ||
    formatToLT(estItem?.stayDuration?.[estStayField]);

  return fromEst || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Resolve fuel quantities from a cargo row or its estimate counterpart
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build an object with { hsfo, lsfo, mgo } for arrival OR departure,
 * preferring operations data, falling back to estimate data.
 *
 * @param {"arrival"|"departure"} direction
 * @param {object} item        - operations cargo row
 * @param {object} estItem     - matched estimate row
 * @returns {{ hsfo: number, lsfo: number, mgo: number }}
 */
function resolveFuel(direction, item, estItem) {
  const isArrival = direction === "arrival";

  // Estimate-side fuel from arrivalAndDepartureFuel
  const estFuelBucket = isArrival
    ? estItem?.arrivalAndDepartureFuel?.arrivalFuel
    : estItem?.arrivalAndDepartureFuel?.departureFuel;
  const ef = estFuelBucket || {};

  // Operations-row field names
  const hsfoKey = isArrival ? "arrivalHsfo" : "departureHsfo";
  const lsfoKey = isArrival ? "arrivalLsfo" : "departureLsfo";
  const mgoKey = isArrival ? "arrivalMgo" : "departureMgo";

  // cargoDetails field names (legacy)
  const cdHsfo = isArrival ? "arrivalBunkersHSFO" : "departureBunkersHSFO";
  const cdLsfo = isArrival ? "arrivalBunkersLSFO" : "departureBunkersLSFO";
  const cdMgo = isArrival ? "arrivalBunkersMGO" : "departureBunkersMGO";

  return {
    hsfo: parseQty(item?.[hsfoKey] ?? item?.cargoDetails?.[cdHsfo], ef?.hsfo ?? estItem?.[hsfoKey] ?? estItem?.cargoDetails?.[cdHsfo]),
    lsfo: parseQty(item?.[lsfoKey] ?? item?.cargoDetails?.[cdLsfo], ef?.lsfo ?? estItem?.[lsfoKey] ?? estItem?.cargoDetails?.[cdLsfo]),
    mgo: parseQty(item?.[mgoKey] ?? item?.cargoDetails?.[cdMgo], ef?.mgo ?? estItem?.[mgoKey] ?? estItem?.cargoDetails?.[cdMgo]),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Main: merge operations cargo rows with estimate data
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Merge a list of operations cargo rows with estimate data for dates & fuel.
 *
 * @param {Array}    cargoList          - operations cargo rows
 * @param {Array}    estimatePortList   - estimate port/leg rows (from extractEstimatePortList)
 * @param {Function} convertToUTC      - component-local function for UTC conversion
 * @returns {Array}                    - enriched cargo rows
 */
export function mergeCargoWithEstimate(cargoList, estimatePortList, convertToUTC) {
  if (!cargoList?.length) return cargoList || [];

  return cargoList.map((item, index) => {
    const estItem = findEstimateItem(item, index, estimatePortList);

    // ── Dates ────────────────────────────────────────────────────────────
    let arrivalTime = resolveDateTime("arrival", item, estItem);
    let departureTime = resolveDateTime("departure", item, estItem);

    // Commence port (or any port with only one timestamp): cross-fill
    if (!arrivalTime && departureTime) arrivalTime = departureTime;
    if (!departureTime && arrivalTime) departureTime = arrivalTime;

    // ── Fuel ─────────────────────────────────────────────────────────────
    const arrFuel = resolveFuel("arrival", item, estItem);
    const depFuel = resolveFuel("departure", item, estItem);

    // ── Timezone ─────────────────────────────────────────────────────────
    const timeZone = item?.timeZone || estItem?.timeZone || "+0";

    // ── UTC Z values ─────────────────────────────────────────────────────
    const arrivalTimeZ =
      formatToLT(item?.arrivalTimeZ) ||
      (arrivalTime && convertToUTC ? convertToUTC(arrivalTime, timeZone) : null);
    const departureTimeZ =
      formatToLT(item?.departureTimeZ) ||
      (departureTime && convertToUTC ? convertToUTC(departureTime, timeZone) : null);

    return {
      ...item,
      arrivalTime,
      departureTime,
      arrivalHsfo: arrFuel.hsfo,
      arrivalLsfo: arrFuel.lsfo,
      arrivalMgo: arrFuel.mgo,
      departureHsfo: depFuel.hsfo,
      departureLsfo: depFuel.lsfo,
      departureMgo: depFuel.mgo,
      timeZone,
      arrivalTimeZ,
      departureTimeZ,
    };
  });
}
